export interface SanitizePriceOptions {
  min?: number;
  max?: number;
  maxFractionDigits?: number; // if undefined, do not trim fractional part
}

export function sanitizePriceInput(
  raw: string,
  options: SanitizePriceOptions = {},
): string {
  if (raw === null || raw === undefined) return "";

  const { min, max } = options;
  const maxFractionDigits = options.maxFractionDigits;

  let s = String(raw);

  // Normalize various spaces to regular space
  s = s.replace(/[\u00A0\u2009\u202F]/g, " ");

  // Allow only digits and common decimal/thousand separators
  // Allowed: digits, dot, comma, spaces, apostrophes (straight and curly)
  s = s.replace(/[^0-9.,'’\s]/g, "");

  // Trim outer whitespace and drop interior spaces/apostrophes (do not keep thousand separators in live input)
  s = s.trim();
  s = s.replace(/[\s’']/g, "");

  if (s === "") return "";

  // If there are multiple decimal separators ('.' and/or ',') keep only the rightmost one
  const dotMatches = [...s.matchAll(/\./g)];
  const commaMatches = [...s.matchAll(/,/g)];

  const lastDot = dotMatches.length
    ? (dotMatches[dotMatches.length - 1]!.index as number)
    : -1;
  const lastComma = commaMatches.length
    ? (commaMatches[commaMatches.length - 1]!.index as number)
    : -1;

  const totalSeps = dotMatches.length + commaMatches.length;
  if (totalSeps <= 1) {
    // With 0 or 1 separator, we may still need to clamp/trim
    return applyClampAndTrim(s, { min, max, maxFractionDigits });
  }

  const keepPos = Math.max(lastDot, lastComma);
  const keepSep = lastDot > lastComma ? "." : ",";

  let out = "";
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (ch === "." || ch === ",") {
      if (i === keepPos) out += keepSep; // keep only the chosen decimal separator
    } else {
      out += ch;
    }
  }

  return applyClampAndTrim(out, { min, max, maxFractionDigits });
}

function applyClampAndTrim(
  input: string,
  { min, max, maxFractionDigits }: SanitizePriceOptions,
): string {
  // Now input has at most one separator
  const sepIdx = Math.max(input.lastIndexOf("."), input.lastIndexOf(","));
  if (sepIdx >= 0) {
    const sep = input[sepIdx]!;
    const left = input.slice(0, sepIdx).replace(/\D+/g, "");
    let right = input.slice(sepIdx + 1).replace(/\D+/g, "");

    if (typeof maxFractionDigits === "number") {
      if (right.length > maxFractionDigits) {
        right = right.slice(0, maxFractionDigits);
      }
    }

    // Clean left leading zeros but keep single zero
    let cleanLeft = left.replace(/^0+(\d)/, "$1");
    if (/^0+$/.test(cleanLeft)) {
      cleanLeft = "0";
    }

    const result = (cleanLeft || "") + sep + right;

    // Preserve trailing separator during typing (e.g., "3.")
    if (right.length === 0) return result;

    // Attempt clamp without reformatting user input (preserve typed zeros)
    const normalized = result.replace(",", ".");
    const num = parseFloat(normalized);
    if (Number.isFinite(num)) {
      if (typeof min === "number" && num < min) return String(min);
      if (typeof max === "number" && num > max) return String(max);
    }

    return result;
  }

  // No separator: digits only path
  let digits = input.replace(/\D+/g, "");
  if (digits === "") return "";
  digits = digits.replace(/^0+(\d)/, "$1");
  if (/^0+$/.test(digits)) digits = "0";

  const num = Number(digits);
  if (Number.isFinite(num)) {
    if (typeof min === "number" && num < min) return String(min);
    if (typeof max === "number" && num > max) return String(max);
  }
  return digits;
}
